<template>
    <div
        :class="{ 'bg-dark-lighter': hasFocus, 'hidden': inputType === 'hidden' }"
        class="scrollableContent bg-dark-light flex w-full items-center rounded-lg p-3"
        ref="element"
    >
        <div class="flex grow flex-col">
            <div class="text-xxs font-bold tracking-widest uppercase" @mousedown.prevent="$refs.input.focus()">
                <span>{{ label }}</span>
                <span v-if="formatDisplayedValue.unit"> ({{ formatDisplayedValue.unit }})</span>
                <RequiredText v-if="required"> *</RequiredText>
            </div>

            <component
                :is="inputType === 'textarea' ? 'textarea' : 'input'"
                ref="input"
                v-bind="attrs"
                class="border-none bg-transparent pl-0 text-white outline-hidden focus:ring-0 focus:outline-hidden"
                :class="{
                    'has-value': modelValue !== emptyValue,
                    'h-6': inputType !== 'textarea',
                }"
                :type="inputType"
                :value="formatDisplayedValue.value"
                :required="required"
                @input="formatRealValue($event.target.value)"
                @change="formatRealValue($event.target.value)"
                @focus="setFocus(true)"
                @blur="
                    () => {
                        setFocus(false)
                        leaveInput()
                    }
                "
                @keyup.enter="
                    () => {
                        if (inputType !== 'textarea') leaveInput()
                    }
                "
                :rows="rows"
            />
        </div>

        <a
            @mousedown.prevent="showPassword()"
            class="text-text-gray right-10 cursor-pointer"
            v-if="type === 'password' && passwordHidden"
        >
            <EyeSlashIcon class="h-6 w-6" />
        </a>

        <a
            @mousedown.prevent="hidePassword()"
            class="text-text-gray right-10 cursor-pointer"
            v-if="type === 'password' && !passwordHidden"
        >
            <EyeIcon class="h-6 w-6" />
        </a>

        <XCircleIcon
            v-if="xcirclebutton"
            v-show="modelValue !== emptyValue"
            class="ml-3 h-4 w-4 cursor-pointer text-gray-500"
            @mousedown.prevent="emits('update:modelValue', emptyValue)"
        />
    </div>
</template>

<script setup>
import { useAttrs, ref, computed, onMounted } from 'vue'
import { convertValueUnit, getUserUnit, toDatetimeLocal, fromDatetimeLocal } from '@/lib/helpers/convert'

import { XCircleIcon } from '@heroicons/vue/24/solid'
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import RequiredText from '@/components/form/RequiredText.vue'

const props = defineProps({
    modelValue: { type: [String, Number, Date], default: '' },
    decimals: { type: Number, default: undefined },
    unit: { type: String, default: null },
    userUnit: { type: String, default: null },
    label: { type: String, default: '' },
    type: { type: String, required: true },
    required: { type: Boolean, default: false },
    keepfocus: { type: Boolean, default: undefined },
    autofocus: { type: Boolean, default: false },
    xcirclebutton: { type: Boolean, default: true },
    emptyValue: { type: [String, Number, Date], default: '' },
    rows: { type: Number, default: 3 },
})
const emits = defineEmits(['update:modelValue', 'input:leave'])

// Accessing undeclared attributes
const attrs = useAttrs()

// Set inheritAttrs to false to prevent attributes being automatically added to the root element
defineOptions({
    inheritAttrs: false,
})

const hasFocus = ref(props.keepfocus === undefined ? false : props.keepfocus)
const input = ref(undefined)
const element = ref(undefined)

const setFocus = (newState) => {
    if (props.keepfocus === undefined) {
        hasFocus.value = newState
    }
}

onMounted(() => {
    if (props.autofocus) input.value.focus()
})

const passwordHidden = ref(true)
const inputType = ref(props.type)

const userUnit = computed(() => (props.unit && props.userUnit ? getUserUnit(props.unit, props.userUnit) : null))

const formatDisplayedValue = computed(() => {
    if (inputType.value === 'datetime-local') {
        return { value: toDatetimeLocal(props.modelValue) } //formatted to be same as convertValueUnit
    }
    return convertValueUnit(props.modelValue, props.unit, userUnit.value, props.decimals, true)
})

const formatRealValue = async (value) => {
    if (inputType.value === 'datetime-local') {
        return emits('update:modelValue', fromDatetimeLocal(value))
    }
    return emits('update:modelValue', convertValueUnit(value, userUnit.value, props.unit, null, true).value)
}

const showPassword = () => {
    // console.log(props.type)
    passwordHidden.value = false
    inputType.value = 'text'
}

const hidePassword = () => {
    passwordHidden.value = true
    inputType.value = 'password'
}

const leaveInput = () => {
    emits('input:leave')
}
</script>
